import React, { useEffect, useMemo, useState } from 'react';
import { Megaphone, AlertTriangle, AlertOctagon, X } from 'lucide-react';
import { listMyAnnouncements, dismissAnnouncement } from '../lib/db';
import { useAuth } from '../contexts/AuthContext';

const SEVERITY = {
  info: {
    Icon:      Megaphone,
    gradient:  'bg-gradient-to-r from-indigo-600 via-indigo-500 to-blue-600',
    text:      'text-white',
    iconAnim:  '',
    shimmer:   true,
  },
  warning: {
    Icon:      AlertTriangle,
    gradient:  'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500',
    text:      'text-white',
    iconAnim:  'announce-pulse',
    shimmer:   true,
  },
  critical: {
    Icon:      AlertOctagon,
    gradient:  'bg-gradient-to-r from-red-600 via-rose-600 to-red-700',
    text:      'text-white',
    iconAnim:  'announce-pulse',
    shimmer:   true,
  },
};

// sessionStorage key for per-session dismissals. We keep a JSON Set of
// announcement ids so the banner can re-render correctly after each fetch.
const SESSION_DISMISSED_KEY = 'announcement_session_dismissals_v1';

function readSessionDismissed() {
  try {
    const raw = sessionStorage.getItem(SESSION_DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function writeSessionDismissed(set) {
  try {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, JSON.stringify(Array.from(set)));
  } catch { /* ignore */ }
}

export default function AnnouncementsBanner() {
  const { user } = useAuth();
  const [items,  setItems]  = useState([]);
  const [busyId, setBusyId] = useState(null);
  // Track session-dismissed ids in state so a click re-renders the banner.
  const [sessionDismissed, setSessionDismissed] = useState(() => readSessionDismissed());

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listMyAnnouncements().then(({ data, error }) => {
      if (cancelled) return;
      if (!error) setItems(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Filter out session-dismissed ones for the current render.
  const visible = useMemo(
    () => items.filter((a) => !sessionDismissed.has(a.id)),
    [items, sessionDismissed],
  );

  if (!visible.length) return null;

  const dismiss = async (a) => {
    // Mode 'none' shouldn't render an X — defensive guard if it ever does.
    if (a.dismiss_mode === 'none') return;

    if (a.dismiss_mode === 'session') {
      const next = new Set(sessionDismissed);
      next.add(a.id);
      setSessionDismissed(next);
      writeSessionDismissed(next);
      return;
    }

    // Default = 'persistent': optimistic remove + RPC write.
    setBusyId(a.id);
    const prev = items;
    setItems((cur) => cur.filter((x) => x.id !== a.id));
    const { error } = await dismissAnnouncement(a.id);
    setBusyId(null);
    if (error) setItems(prev);
  };

  return (
    <div className="space-y-1.5 px-4 pt-1 -mt-1">
      {visible.map((a, idx) => {
        const meta = SEVERITY[a.severity] || SEVERITY.info;
        const Icon = meta.Icon;
        const canDismiss = a.dismiss_mode !== 'none';
        // Build the marquee message: title in bold + body after an em-dash.
        // We render the same fragment twice so the looped animation wraps
        // seamlessly when the inner track translates to -50%.
        const messageFragment = (
          <span className="inline-flex items-center gap-2 align-middle text-sm leading-tight">
            <strong className="font-semibold tracking-tight">{a.title}</strong>
            {a.body && (
              <>
                <span className="opacity-70">—</span>
                <span className="opacity-95 whitespace-pre">{a.body}</span>
              </>
            )}
          </span>
        );
        return (
          <div
            key={a.id}
            role="status"
            style={{ animationDelay: `${idx * 60}ms` }}
            className={[
              'relative overflow-hidden rounded-md shadow-sm ring-1 ring-white/10',
              'flex items-center gap-2 pl-2.5 pr-1 py-1',
              meta.gradient,
              meta.text,
              'announce-in announce-glow',
              meta.shimmer ? 'announce-shimmer' : '',
            ].filter(Boolean).join(' ')}
          >
            <span className={`flex-shrink-0 ${meta.iconAnim}`}>
              <Icon size={13} />
            </span>

            {/* Ticker viewport — clips the moving track. The track holds
                the same message twice; at -50% the second copy occupies the
                viewport, making the loop seamless. */}
            <div className="flex-1 min-w-0 overflow-hidden relative z-10">
              <div
                className="announce-ticker-track inline-flex items-center"
                style={{ gap: '4rem' }}
              >
                {messageFragment}
                {messageFragment}
              </div>
            </div>

            {canDismiss ? (
              <button
                onClick={() => dismiss(a)}
                disabled={busyId === a.id}
                aria-label="Dismiss announcement"
                title={a.dismiss_mode === 'session' ? 'Hide for this session' : 'Dismiss permanently'}
                className="relative z-10 p-0.5 rounded hover:bg-white/20 active:bg-white/30 transition disabled:opacity-50 flex-shrink-0"
              >
                <X size={12} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
